import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { FormProvider, useForm } from "react-hook-form";
import {
  normalizeHour,
  RHFAutocomplete,
  RHFTextField,
} from "../../../../RHFComponents/RHFComponents";
import { MedintDataContext } from "../../../Context/MedintContextWrapper";
import { ReactComponent as ModifyIcon } from "../../../../../assets/icons/MedintModifyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../../assets/icons/MedintDeleteIcon.svg";
import { ReactComponent as AddIcon } from "../../../../../assets/icons/MedintAddIcon.svg";
import MedintBoxDetailModal from "./MedintBoxModalDetail";
import { medboxd, medboxt, ricmedbox } from "../../../../../networking";
import dayjs from "dayjs";
import { StyledDataGrid } from "../../../../AppointmentManagement/AppointmentManagementDetail/AppointmentManagementDetail";
import { GridColDef } from "@mui/x-data-grid";
import SortIcon from "@mui/icons-material/Sort";
import { ValueSetter } from "date-fns/parse/_lib/Setter";
import { formatHour } from "../../../../AppointmentResearch/AppointmentResearch";
import { AllertMessage } from "../MedintDetails";

//Funzioni per formattare le date e le ore nel formato desiderato
//DD/MM/YY e HH:MM
export const fmtDate = (iso?: string) =>
  iso ? dayjs(iso).format("DD/MM/YY") : "";

type RigaBox = {
  id: number;
  "codice medico"?: string;
  dataInizio: string;
  oraInizio: string;
  dataFine: string;
  oraFine: string;
  ritiroReferto: string;
  medico: string;
};

type MedintBoxModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
};

type BoxFormValues = {
  medicoRif: string | null;
  codiceMedico: string | null;
  filtroMedico: string | null;
};

type MedBoxRow = {
  MEDBOXD_DATA_CREA: string;
  MEDBOXD_ORA_CREA: string;
  MEDBOXD_CODOPE_CODICE_CREA: string;
  MEDBOXD_DATA_MOD: string;
  MEDBOXD_ORA_MOD: string;
  MEDBOXD_CODOPE_CODICE_MOD: string;
  TABAMB_CODICE: string;
  MEDBOXD_PRGINT: string;
  MEDBOXD_DATA_INI: string;
  MEDBOXD_ORA_INI: string;
  MEDBOXD_DATA_FINE: string;
  MEDBOXD_ORA_FINE: string;
  MEDBOXD_DT_RITIRO: string;
  MEDINT_CODICE?: string;
  DECODE_MEDINT_MEDINT_COGNOME?: string;
  DECODE_MEDINT_MEDINT_NOME?: string;
};

const BLUE = "#003267";
const HEADER_BLUE = "#003267";

export default function MedintBoxModal({
  open,
  onClose,
  onConfirm,
}: MedintBoxModalProps) {
  const handleDeleteRow = async (data: any) => {
    console.log({ data });
    try {
      const response = await medboxd({
        crud: "d",
        prgint: data.MEDBOXD_PRGINT,
      });

      console.log({ response });
    } catch (error) {
      console.error("Failed to delete element");
    } finally {
      await getMedBoxData();
    }
  };

  const medBoxtCols: GridColDef[] = [
    { field: "dataInizio", headerName: "Data Inizio", flex: 0.5 },
    { field: "oraInizio", headerName: "Ora Inizio", flex: 0.5 },
    { field: "dataFine", headerName: "Data Fine", flex: 0.5 },
    { field: "oraFine", headerName: "Ora Fine", flex: 0.5 },
    { field: "ritiroReferto", headerName: "Ritiro Referto", flex: 0.5 },
    { field: "medico", headerName: "Medico", flex: 1.5 },
    {
      field: "azioni",
      headerAlign: "center",
      align: "center",
      headerName: "Azioni",
      flex: 0.5,
      renderCell: (params: any) => (
        <>
          <IconButton
            size="small"
            aria-label="Modifica"
            onClick={() => {
              const raw = medBoxData[params.id];
              if (raw) openEdit(raw);
            }}
          >
            <ModifyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Elimina"
            sx={{ color: "#d32f2f" }}
            onClick={() => {
              const raw = medBoxData[params.id];
              handleDeleteRow(raw);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const context = useContext(MedintDataContext);

  const [tab, setTab] = useState(0);

  // state per riconoscere se arrivano dati dalla tabella medBoxD associata al medico o necessario comunicare al db la sua creazione
  // 0 fase crezione, 1 fase di modifica, aggiunta
  const [pageStatus, setPageStatus] = useState(0);
  const [dataMedBoxUpload, setDataMedBoxUpload] = useState(false);

  //Stati per la gestione della componente dell'allert
  const [showAllert, setShowAllert] = useState<boolean>(false);
  const [severity, setSeverity] = useState<string>("warning");
  const [message, setMessage] = useState<string>(
    "Si è verificato un problema sulla pagina",
  );

  const [openDetail, setOpenDetail] = useState(false);
  const [filterApply, setFilterApply] = useState(false);
  const [detailMode, setDetailMode] = useState<"create" | "edit">("create");
  const [detailInitial, setDetailInitial] = useState<any>(null);
  const [medBoxData, setMedBoxData] = useState<MedBoxRow[]>([]);

  const methods = useForm<BoxFormValues>({
    defaultValues: {
      medicoRif: "",
      codiceMedico: null,
      filtroMedico: "",
    },
  });

  const filtroMedico = methods.watch("filtroMedico");

  const getMedBoxTabella = async () => {
    try {
      const response = await medboxt({
        crud: "r",
        medint_prgint: context?.doctorAgenda.medico[0].IDMEDICO ?? "",
      });
      console.log("Dati Medint Box Tabella ricevuti:", response.data);
      return response;
    } catch (error) {
      console.error("Errore nel recupero dei dati Medint Box Tabella:", error);
    }
  };

  const getMedBoxData = async () => {
    setDataMedBoxUpload(false);
    try {
      const response = await ricmedbox({
        medint_prgint: context?.doctorAgenda.medico[0].IDMEDICO ?? "",
      });
      if (!open) return;
      setMedBoxData(response.data?.dati?.medboxt?.medboxd ?? []);
      console.log("Dati Medint Box ricevuti:", response.data);
    } catch (error) {
      console.error("Errore nel recupero dei dati Medint Box:", error);
    } finally {
      setDataMedBoxUpload(true);
    }
  };

  const createMedBoxTable = async () => {
    try {
      const response = await medboxt({
        crud: "c",
        dati: {
          medboxt: {
            medint_prgint: context?.doctorAgenda.medico[0].IDMEDICO ?? "",
            MEDINT_CODICE: context?.doctorAgenda.medico[0].CODICEMEDICO ?? "",
          },
        },
      });
      console.log("Fetch di creazione del medBoxT:", response.data);
      return response;
    } catch (error) {
      console.error("Errore nel recupero dei dati Medint Box:", error);
    }
  };

  useEffect(() => {
    if (!open) return;

    console.log("MedintBoxModal context:", context);

    const m = context?.doctorAgenda?.medico?.[0];
    if (!m) return;

    const defaultId = m.IDMEDICO ? String(m.IDMEDICO) : null;
    const currentId = methods.getValues("codiceMedico");

    methods.reset({
      medicoRif: `${m.COGNOMEMEDICO ?? ""} ${m.NOMEMEDICO ?? ""}`.trim(),
      codiceMedico: currentId ?? defaultId,
      filtroMedico: "",
    });

    getMedBoxTabella().then((value: any) => {
      if (value.data.esito === "OK") {
        getMedBoxData();
        setPageStatus(1);
      } else {
        setDataMedBoxUpload(true);
        console.log("Necessaria creazione inizialmente");
      }
    });
  }, [open, context?.doctorAgenda, methods]);

  // Mappatura dei dati ricevuti in righe della tabella
  const rowsBoxData: RigaBox[] = useMemo(() => {
    if (!Array.isArray(medBoxData)) return [];

    return medBoxData.map((row: any, index: number) => ({
      id: index,
      "codice medico": row.MEDINT_CODICE,
      dataInizio: fmtDate(row.MEDBOXD_DATA_INI) || "",
      oraInizio: normalizeHour(row.MEDBOXD_ORA_INI) || "",
      dataFine: fmtDate(row.MEDBOXD_DATA_FINE) || "",
      oraFine: normalizeHour(row.MEDBOXD_ORA_FINE) || "",
      ritiroReferto: fmtDate(row.MEDBOXD_DT_RITIRO) || "",
      medico:
        `${row?.DECODE_MEDINT_MEDINT_COGNOME === "" || row?.DECODE_MEDINT_MEDINT_COGNOME === null ? "null" : row?.DECODE_MEDINT_MEDINT_COGNOME} ${
          row?.DECODE_MEDINT_MEDINT_NOME === "" ||
          row?.DECODE_MEDINT_MEDINT_NOME === null
            ? "null"
            : row?.DECODE_MEDINT_MEDINT_NOME
        }`.trim(),
    }));
  }, [medBoxData]);

  //Funzione per costruire i valori iniziali del dettaglio box (fase di modifica)
  const buildDetailInitialFromRow = (raw: MedBoxRow) => ({
    // dataInizio: fmtDate(raw.MEDBOXD_DATA_INI) ?? null,
    // dataFine: fmtDate(raw.MEDBOXD_DATA_FINE) ?? null,

    dataInizio: raw.MEDBOXD_DATA_INI ?? null,
    dataFine: raw.MEDBOXD_DATA_FINE ?? null,
    dataPrevistaRitiroReferto: raw.MEDBOXD_DT_RITIRO ?? null,

    dataInserimento: raw.MEDBOXD_DATA_CREA ?? null,
    dataModifica: raw.MEDBOXD_DATA_MOD ?? null,

    // dataPrevistaRitiroReferto: fmtDate(raw.MEDBOXD_DT_RITIRO) ?? null,
    medboxdPrgint: raw.MEDBOXD_PRGINT,

    oraInizio: normalizeHour(raw.MEDBOXD_ORA_INI),
    // ? formatHour(
    //     `${raw.MEDBOXD_ORA_INI.slice(0, 2)}:${raw.MEDBOXD_ORA_INI.slice(2)}`,
    //   )
    // : null,
    oraFine: normalizeHour(raw.MEDBOXD_ORA_FINE),
    // ? formatHour(
    //     `${raw.MEDBOXD_ORA_FINE.slice(0, 2)}:${raw.MEDBOXD_ORA_FINE.slice(2)}`,
    //   )
    // : null,

    // dataInserimento: fmtDate(raw.MEDBOXD_DATA_CREA) ?? null,
    oraInserimento: normalizeHour(raw.MEDBOXD_ORA_CREA),
    // ? formatHour(
    //     `${raw.MEDBOXD_ORA_CREA.slice(0, 2)}:${raw.MEDBOXD_ORA_CREA.slice(2)}`,
    //   )
    // : null,
    operatoreInserimento: raw.MEDBOXD_CODOPE_CODICE_CREA ?? "",

    // dataModifica: fmtDate(raw.MEDBOXD_DATA_MOD) ?? null,
    oraModifica: normalizeHour(raw.MEDBOXD_ORA_MOD),
    // ? formatHour(
    //     `${raw.MEDBOXD_ORA_MOD.slice(0, 2)}:${raw.MEDBOXD_ORA_MOD.slice(2)}`,
    //   )
    // : null,
    operatoreModifica: raw.MEDBOXD_CODOPE_CODICE_MOD ?? "",

    ambulatorio: raw.TABAMB_CODICE ? String(raw.TABAMB_CODICE) : null,
  });

  //Funzione per aprire il dettaglio in modalità modifica
  const openEdit = (raw: MedBoxRow) => {
    setDetailMode("edit");
    setDetailInitial(buildDetailInitialFromRow(raw));
    setOpenDetail(true);
  };

  //Funzione per la gestione dell'evento invocato al click di Inserisci
  const handleInserisci = async () => {
    try {
      if (pageStatus === 0) {
        await createMedBoxTable().then((value: any) => {
          setMessage(
            value.data?.messaggio ||
              "Errore durante la creazione di una MEDBOXT",
          );
          setShowAllert(true);
          if (value.data.esito === "OK") {
            console.log("Aperto il medBoxD a seguito,", value.data.messaggio);
            setDetailMode("create");
            setDetailInitial(null);
            setOpenDetail(true);
            setPageStatus(1);
          }
        });
      } else {
        console.log("Apro direttamente la gestione di medboxd");
        setDetailMode("create");
        setDetailInitial(null);
        setOpenDetail(true);
      }
    } catch (error) {
      console.error("Errore sull'handleInserisci");
    } finally {
      setTimeout(() => {
        setShowAllert(false);
      }, 4000);
    }
  };

  // Da separare le componenti, questo può causare errori
  useEffect(() => {
    setPageStatus(0);
    setFilterApply(false);
    setDataMedBoxUpload(false);
    methods.setValue("filtroMedico", null);
    setMedBoxData([]);
  }, [onClose]);

  return (
    <Box>
      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
      />
      {openDetail && !showAllert && (
        <MedintBoxDetailModal
          open={openDetail}
          mode={detailMode}
          initialValues={detailInitial}
          onBack={() => setOpenDetail(false)}
          onConfirm={(data) => {
            console.log("Submit Box Dettaglio:", data);
            if (detailMode === "edit") {
              setOpenDetail(false);
              getMedBoxData();
            } else {
              getMedBoxData();
            }
          }}
        />
      )}
      <Modal open={open} onClose={onClose}>
        <FormProvider {...methods}>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              fontFamily: "Montserrat",
            }}
          >
            <Paper
              elevation={3}
              sx={{
                width: "min(1100px, 96vw)",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {/* Header Tabs */}
              <Box sx={{ px: 3, pt: 2 }}>
                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  aria-label="Box"
                  sx={{
                    minHeight: 38,
                    "& .MuiTabs-indicator": {
                      backgroundColor: BLUE,
                      height: 3,
                      borderRadius: 3,
                    },
                    "& .MuiTab-root": {
                      minHeight: 38,
                      textTransform: "none",
                      color: `${BLUE} !important`,
                      px: 0,
                      mr: 3,
                      fontFamily: "Montserrat",
                      fontWeight: "bold",
                    },
                    "& .Mui-selected": { color: BLUE },
                  }}
                >
                  <Tab id="dettaglioOrariFissi" label="Box" />
                </Tabs>
              </Box>

              {/* Body */}
              <Box sx={{ p: 3, overflowY: "auto", flex: 1, minHeight: 0 }}>
                <Box sx={{ display: "grid", gap: 2, maxWidth: 420 }}>
                  <RHFTextField
                    control={methods.control}
                    name="medicoRif"
                    label="Medico / Risorsa di Riferimento"
                    fullWidth
                    disabled
                  />
                  <RHFAutocomplete
                    control={methods.control}
                    name="codiceMedico"
                    label="Codice Medico"
                    options={context?.doctorsData ?? []}
                    keyField="IDMEDICO"
                    getOptionLabel={(d: any) =>
                      `${d.CODICEMEDICO ?? d["codice medico"] ?? ""} - ${
                        d.COGNOMEMEDICO ?? d.cognome ?? ""
                      } ${d.NOMEMEDICO ?? d.nome ?? ""}`.trim()
                    }
                    mapIn={(id, opts) =>
                      opts.find(
                        (o: any) => String(o.IDMEDICO ?? o.pkey) === String(id),
                      ) ??
                      context?.doctorsData?.find(
                        (e) =>
                          e["codice medico"] ===
                          context?.selectedDoctor?.["codice medico"],
                      ) ??
                      null
                    }
                    mapOut={(opt: any) =>
                      String(opt?.IDMEDICO ?? opt?.pkey ?? "") || null
                    }
                  />
                </Box>

                {/* Box centrale con griglia */}
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  {/* Riga input + filtro + Inserimento */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <RHFAutocomplete
                      control={methods.control}
                      {...( { disableClearable: true } as any )}
                      name="filtroMedico"
                      label={"Filtro Medico"}
                      {...({ disableClearable: true, popupIcon: null } as any)}
                      sx={{
                        "&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot":
                          {
                            paddingRight: "6px",
                          },
                      }}
                      fullWidth
                      options={context?.doctorsData ?? []}
                      keyField="IDMEDICO"
                      size="small"
                      componentsProps={{
                        clearIndicator: {
                          onClick: (event) => {
                            setFilterApply(false);
                            methods.setValue("filtroMedico", null);
                          },
                        },
                      }}
                      customInputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ mr: 0, ml: 0 }}>
                            <IconButton
                              aria-label="Filtro"
                              edge="end"
                              size="small"
                              onClick={() => {
                                setFilterApply(!filterApply);
                              }}
                            >
                              <FilterAltOutlinedIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      getOptionLabel={(d: any) =>
                        `${d.CODICEMEDICO ?? d["codice medico"] ?? ""} - ${
                          d.COGNOMEMEDICO ?? d.cognome ?? ""
                        } ${d.NOMEMEDICO ?? d.nome ?? ""}`.trim()
                      }
                      mapIn={(id, opts) =>
                        opts.find(
                          (o: any) =>
                            String(o.CODICEMEDICO ?? o?.["codice medico"]) ===
                            String(id),
                        ) ?? null
                      }
                      mapOut={(opt: any) =>
                        String(
                          opt?.CODICEMEDICO ?? opt?.["codice medico"] ?? "",
                        ) || null
                      }
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      sx={{
                        height: 36,
                        px: 2,
                        borderRadius: 1,
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => {
                        handleInserisci();
                      }}
                    >
                      Inserisci
                    </Button>
                  </Box>

                  {/* Tabella */}
                  <StyledDataGrid
                    rows={
                      filterApply
                        ? rowsBoxData.filter(
                            (e) => e["codice medico"] === filtroMedico,
                          )
                        : rowsBoxData
                    }
                    columns={medBoxtCols}
                    loading={!dataMedBoxUpload}
                    getRowId={(r) => r.id}
                    sortingOrder={["desc", "asc", null]}
                    sx={{
                      height: 260,
                      '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                        {
                          display: "none",
                        },
                    }}
                  />
                </Paper>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlinedError"
                  color="error"
                  onClick={onClose}
                  sx={{
                    borderRadius: 1,
                    fontWeight: "bold",
                    px: 2.5,
                  }}
                >
                  Uscita
                </Button>

                <Button
                  variant="contained"
                  disabled={!dataMedBoxUpload}
                  onClick={onConfirm}
                  sx={{
                    borderRadius: 1,
                    fontWeight: "bold",
                    px: 2.5,
                    backgroundColor: "#2E83C4",
                    "&:hover": { backgroundColor: "#276fa5" },
                  }}
                >
                  Conferma
                </Button>
              </Box>
            </Paper>
          </Box>
        </FormProvider>
      </Modal>
    </Box>
  );
}
